import { Grid, Modal } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { useDisclosure } from '@mantine/hooks'
import { FormattedMessage } from 'react-intl'
import { IntegrationListItem } from '@/components/integrations/IntegrationListItem/IntegrationListItem'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { EventDestination } from '@/types/deployment'
import { FormValues } from '../../IntegrationSelection'
import { integrationConfig } from '../../config'
import { MqttBrokerForm } from './MqttBrokerForm/MqttBrokerForm'
import { MqttBrokerInfo } from './MqttBrokerInfo/MqttBrokerInfo'
import { MqttTopicList } from './MqttTopicList/MqttTopicList'
import { MqttTopicsForm } from './MqttTopicsForm/MqttTopicsForm'

type MqttIntegrationProp = {
  deviceId: string
  form: UseFormReturnType<FormValues>
  disabled?: boolean
}

export const MqttIntegration = ({
  deviceId,
  form,
  disabled
}: MqttIntegrationProp) => {
  const { isSelected, attributes } = form.getValues().integrations.mqtt
  const [
    isBrokerModalOpened,
    { open: openBrokerModal, close: closeBrokerModal }
  ] = useDisclosure(false)

  const [isTopicModalOpened, { open: openTopicModal, close: closeTopicModal }] =
    useDisclosure(false)

  return (
    <IntegrationListItem
      checkboxId="mqtt"
      isSelected={isSelected}
      disabled={disabled}
      title={
        <FormattedMessage id={integrationConfig[EventDestination.Mqtt].name} />
      }
      description={
        <FormattedMessage
          id={integrationConfig[EventDestination.Mqtt].description}
        />
      }
      logoSrc={integrationConfig[EventDestination.Mqtt].logo}
      showDetails={isSelected}
      contentWidth={800}
      onChange={(value) => {
        form.setFieldValue('integrations.mqtt.isSelected', value)
        form.clearErrors()
      }}
    >
      <PlatformAlert mb="xl" compact>
        <FormattedMessage id="integrations.mqtt.instructions" />
      </PlatformAlert>

      <Grid gutter="xl">
        <Grid.Col span={6}>
          <MqttBrokerInfo
            prefix="mqtt://" // In V1 only mqtt:// is supported
            brokerUrl={attributes.broker_address}
            port={attributes.broker_port}
            onEdit={openBrokerModal}
            onAdd={openBrokerModal}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <MqttTopicList
            topics={attributes.topics.map((topic) => topic.name)}
            onEdit={openTopicModal}
            onAdd={openTopicModal}
          />
        </Grid.Col>
      </Grid>

      <Modal
        size="xl"
        title={<FormattedMessage id="integrations.mqtt.broker" />}
        opened={isBrokerModalOpened}
        onClose={closeBrokerModal}
      >
        <MqttBrokerForm
          deviceId={deviceId}
          initialValues={{
            protocol: 'mqtt', // Currently, only MQTT is supported, and we neither send nor receive it from the backend.
            url: attributes.broker_address,
            port: attributes.broker_port,
            qos: attributes.broker_qos
          }}
          onSubmit={(values) => {
            form.setFieldValue('integrations.mqtt.attributes', {
              broker_address: values.url,
              broker_port: values.port,
              broker_qos: values.qos,
              topics: attributes.topics
            })

            closeBrokerModal()
          }}
          onCancel={closeBrokerModal}
        />
      </Modal>

      <Modal
        size="xl"
        title={<FormattedMessage id="integrations.mqtt.topics" />}
        opened={isTopicModalOpened}
        onClose={closeTopicModal}
      >
        <MqttTopicsForm
          initialValues={{
            topics: attributes.topics
          }}
          onSubmit={(values) => {
            form.setFieldValue('integrations.mqtt.attributes', {
              ...attributes,
              topics: values.topics
            })

            closeTopicModal()
          }}
          onCancel={closeTopicModal}
        />
      </Modal>
    </IntegrationListItem>
  )
}
